import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs) || '');
}

function normalizeStrings(strings: string[]): string {
  const lineCommentPattern = /\/\/.*((\r?\n)|$)/g;

  let result = strings.join(' ');

  if (result.includes('//')) {
    result = result.replace(lineCommentPattern, '');
  }

  return result.replace(/\s+/g, ' ').trim();
}

export function handleCLs(
  strings: TemplateStringsArray,
  ...expressions: ClassValue[]
): string {
  const classNamesList: string[] = strings.reduce(
    (previous, current, currentIndex) => {
      const expression = expressions[currentIndex];
      const resolvedClass = clsx(expression) || '';

      previous.push(current, resolvedClass);

      return previous;
    },
    [] as string[],
  );

  return normalizeStrings(classNamesList);
}

export function cls(
  strings: TemplateStringsArray,
  ...expressions: ClassValue[]
): string {
  return handleCLs(strings, ...expressions);
}

export function capitalizeFirstCharacter(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function capitalizeFirstCharactersForEachWord(
  text: string,
  separator = ' ',
  joiner = ' ',
) {
  return text
    .split(separator)
    .map(word => capitalizeFirstCharacter(word))
    .join(joiner);
}

export function formatForUrl(text: string): string {
  return text.toLowerCase().replace(/\s/g, '-');
}

export function formatForLabel(text: string, lowerCase = true): string {
  const replaceRegex = new RegExp(/_|-/, 'g');

  if (lowerCase) {
    return capitalizeFirstCharactersForEachWord(
      text.toLowerCase().replaceAll(replaceRegex, ' ').replaceAll('and', '&'),
    );
  }

  return text.replaceAll(replaceRegex, ' ').replaceAll('AND', '&');
}

export function deepMerge<T extends object>(base: T, custom: T): T {
  const output = { ...base };

  for (const key in custom) {
    if (
      custom[key] &&
      typeof custom[key] === 'object' &&
      !Array.isArray(custom[key]) &&
      base[key] &&
      typeof base[key] === 'object'
    ) {
      output[key] = deepMerge(base[key], custom[key]);
    } else {
      output[key] = custom[key];
    }
  }

  return output;
}

export function handlePlural(baseText = '', value = 0) {
  return `${baseText}${value > 1 ? 's' : ''}`;
}
